import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Shield, Mail, Loader2, CheckCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const SUBJECTS = [
  "General Inquiry",
  "Technical Support",
  "Billing Question",
  "Partnership Opportunity",
  "Bug Report",
  "Feature Request",
  "Press Inquiry",
  "Other"
];

const ContactPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [accountType, setAccountType] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !accountType || !subject || !message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    
    try {
      await api.post('/api/contact', {
        name,
        email,
        company,
        account_type: accountType,
        subject,
        message
      });
      setSent(true);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F8FF]">
      {/* Header */}
      <header className="border-b border-[#E8EDF5] bg-[#F5F8FF]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-[#1B5AFF]" />
              <span className="text-xl font-bold text-[#0A0F1E]">VETT</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-[#0A0F1E] hover:text-white hover:bg-[#FFFFFF]">
                  Sign in
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-[#0A0F1E] mb-4">Contact Us</h1>
            <p className="text-[#5B6472] text-lg">
              Have a question or need help? We'd love to hear from you.
            </p>
          </div>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8">
            {sent ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-[#0A0F1E] mb-2">Message Sent!</h2>
                <p className="text-[#5B6472] mb-6">
                  Thank you for reaching out. We'll get back to you within 24-48 hours.
                </p>
                <Link to="/">
                  <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl">
                    Back to Home
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-[#0A0F1E]">Name *</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="bg-[#FFFFFF]/50 border-[#E8EDF5] text-white rounded-xl h-12"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#0A0F1E]">Email *</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="bg-[#FFFFFF]/50 border-[#E8EDF5] text-white rounded-xl h-12"
                      required
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-[#0A0F1E]">Company</Label>
                    <Input
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder="Your company (optional)"
                      className="bg-[#FFFFFF]/50 border-[#E8EDF5] text-white rounded-xl h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#0A0F1E]">I am a... *</Label>
                    <Select value={accountType} onValueChange={setAccountType}>
                      <SelectTrigger className="bg-[#FFFFFF]/50 border-[#E8EDF5] text-white rounded-xl h-12">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                        <SelectItem value="employer" className="text-white hover:bg-[#FFFFFF]">
                          Employer
                        </SelectItem>
                        <SelectItem value="employee" className="text-white hover:bg-[#FFFFFF]">
                          Employee
                        </SelectItem>
                        <SelectItem value="other" className="text-white hover:bg-[#FFFFFF]">
                          Other
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0A0F1E]">Subject *</Label>
                  <Select value={subject} onValueChange={setSubject}>
                    <SelectTrigger className="bg-[#FFFFFF]/50 border-[#E8EDF5] text-white rounded-xl h-12">
                      <SelectValue placeholder="Select subject" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                      {SUBJECTS.map((s) => (
                        <SelectItem key={s} value={s} className="text-white hover:bg-[#FFFFFF]">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0A0F1E]">Message *</Label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="How can we help you?"
                    className="bg-[#FFFFFF]/50 border-[#E8EDF5] text-white rounded-xl min-h-[150px]"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl h-12 btn-glow"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </Card>

          {/* Contact Info */}
          <div className="mt-12 text-center">
            <p className="text-[#5B6472]">
              Or email us directly at{' '}
              <a href="mailto:contact@getvett.org" className="text-[#1B5AFF] hover:underline">
                contact@getvett.org
              </a>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E8EDF5] py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#1B5AFF]" />
              <span className="text-[#5B6472]">© 2026 VETT. All rights reserved.</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-[#5B6472] hover:text-white">Privacy Policy</Link>
              <Link to="/terms" className="text-[#5B6472] hover:text-white">Terms of Service</Link>
              <Link to="/cookies" className="text-[#5B6472] hover:text-white">Cookie Policy</Link>
              <Link to="/impressum" className="text-[#5B6472] hover:text-white">Impressum</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
